import React, { useState } from 'react';
import { Box, Button, IconButton, Menu, MenuItem, useMediaQuery, } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import MenuIcon from '@mui/icons-material/Menu';
import { useNavigate } from 'react-router-dom';

import BlitzHeader from './BlitzHeader';
import routes from '../Config/routes';
import useAuth from '../Hooks/use-auth';
import { useCreatorAuth } from '../Hooks/creator-use-auth';

export default function HomeHeader({ menuButton }) {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const { isAuthorized } = useAuth();
  const { isAuthorized: isCreatorAuthorized } = useCreatorAuth();

  const handleDashboardRedirect = () => {
    if (isCreatorAuthorized && !isAuthorized) {
      navigate('/creatorconnect/start');
    } else {
      navigate(routes.dashboard);
    }
  };

  /* ---------------- Mobile menu ---------------- */
  const [mobileAnchorEl, setMobileAnchorEl] = useState(null);
  const handleMenuClick = (e) => setMobileAnchorEl(e.currentTarget);
  const closeMobileMenu = () => setMobileAnchorEl(null);

  const jump = (slug) => () => {
    closeMobileMenu();
    navigate(slug);
  };

  return (
    <BlitzHeader
      sx={{ pt: { xs: 'env(safe-area-inset-top)', sm: 0 } }}
      menuButton={menuButton}
    >
      {/* Spacer so logo + nav align nicely */}
      <Box sx={{ height: 50, flexShrink: 0 }} />

      {/* Center navigation */}
      <Box display="flex" alignItems="center" justifyContent="center" flex={1}>
        {isMobile ? (
          <>
            <IconButton color="inherit" onClick={handleMenuClick}>
              <MenuIcon />
            </IconButton>

            {/* Mobile dropdown */}
            <Menu
              anchorEl={mobileAnchorEl}
              open={Boolean(mobileAnchorEl)}
              onClose={closeMobileMenu}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
              transformOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
              <MenuItem onClick={() => {
                return window.location.href = routes.solutions;
              }}>Pay</MenuItem>
              <MenuItem onClick={() => {
                return window.location.href = routes.create;
              }}>Create</MenuItem>
              <MenuItem
                onClick={() => {
                  return window.location.href = routes.collaborate;
                }}
              >
                Collaborate
              </MenuItem>
            </Menu>
          </>
        ) : (
          <>
            <Button
              color="inherit"
              variant="text"
              onClick={() => {
                return window.location.href = routes.solutions;
              }}
              sx={{
                fontWeight: 600,
                fontSize: "1rem",
                color: "#334155",
                px: 2,
                py: 1,
                borderRadius: "8px",
                transition: "all 0.3s ease",
                '&:hover': {
                  background: "rgba(15,23,42,0.06)",
                  color: "#0f172a",
                  transform: "translateY(-1px)"
                }
              }}
            >
              Pay
            </Button>
            <Button
              color="inherit"
              variant="text"
              onClick={() => {
                return window.location.href = routes.create;
              }}
              sx={{
                fontWeight: 600,
                fontSize: "1rem",
                color: "#334155",
                px: 2,
                py: 1,
                borderRadius: "8px",
                transition: "all 0.3s ease",
                '&:hover': {
                  background: "rgba(15,23,42,0.06)",
                  color: "#0f172a",
                  transform: "translateY(-1px)"
                }
              }}
            >
              Create
            </Button>
            <Button
              color="inherit"
              variant="text"
              onClick={() => {
                return window.location.href = routes.collaborate;
              }}
              sx={{
                fontWeight: 600,
                fontSize: "1rem",
                color: "#334155",
                px: 2,
                py: 1,
                borderRadius: "8px",
                transition: "all 0.3s ease",
                '&:hover': {
                  background: "rgba(15,23,42,0.06)",
                  color: "#0f172a",
                  transform: "translateY(-1px)"
                }
              }}
            >
              Collaborate
            </Button>

          </>
        )}
      </Box>

      {/* Right-hand auth links */}
      <Box
        display="flex"
        justifyContent="end"
        alignItems="center"
        flex={1}
        overflow="visible"
        textAlign="right"
        whiteSpace="nowrap"
        minWidth={0}
        gap={{ xs: 0.5, sm: 1 }}

      >
        {isAuthorized || isCreatorAuthorized ? (
          <Button
            color="inherit"
            variant="text"
            onClick={handleDashboardRedirect}
            sx={{
              fontWeight: 600,
              fontSize: { xs: "0.8125rem", sm: "0.875rem" },
              color: "#334155",
              px: { xs: 1.5, sm: 2 },
              py: { xs: 0.5, sm: 1 },
              '&:hover': {
                color: "#0f172a",
                background: "rgba(15,23,42,0.06)"
              }
            }}
          >
            Back&nbsp;to&nbsp;Blitz
          </Button>
        ) : (
          <>
            <Button
              color="inherit"
              variant="text"
              onClick={() => navigate(routes.login)}
              sx={{
                fontWeight: 600,
                fontSize: { xs: "0.6875rem", sm: "0.9375rem" },
                border: "1px solid #e2e8f0",
                color: "#334155",
                px: { xs: 1.25, sm: 3 },
                py: { xs: 0.375, sm: 1 },
                borderRadius: "50px",
                background: "white",
                minWidth: { xs: "unset", sm: "auto" },
                transition: "all 0.3s ease",
                '&:hover': {
                  background: "#f1f5f9",
                  borderColor: "#cbd5e1",
                  color: "#0f172a",
                  transform: { xs: "none", sm: "translateY(-2px)" }
                }
              }}
            >
              Login
            </Button>
            <Button
              color="inherit"
              variant="text"
              onClick={() => navigate(routes.onboard)}
              sx={{
                fontWeight: 700,
                fontSize: { xs: "0.5875rem", sm: "0.9375rem" },
                px: { xs: 1.25, sm: 3 },
                py: { xs: 0.375, sm: 1 },
                ml: { xs: 0.5, sm: 1 },
                background: "#0f172a",
                border: "1px solid #0f172a",
                boxShadow: { xs: "0 1px 3px rgba(15,23,42,0.12)", sm: "0 2px 8px rgba(15,23,42,0.2)" },
                borderRadius: "50px",
                color: "white",
                minWidth: { xs: "unset", sm: "auto" },
                position: "relative",
                overflow: "hidden",
                transition: "all 0.3s ease",
                '&::before': {
                  content: '""',
                  position: "absolute",
                  top: 0,
                  left: "-100%",
                  width: "100%",
                  height: "100%",
                  background: "linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent)",
                  transition: "left 0.5s",
                },
                '&:hover': {
                  transform: { xs: "none", sm: "translateY(-2px)" },
                  background: "#1e293b",
                  boxShadow: { xs: "0 2px 6px rgba(15,23,42,0.25)", sm: "0 4px 12px rgba(15,23,42,0.3)" },
                  '&::before': {
                    left: "100%"
                  }
                }
              }}
            >
              Sign Up
            </Button>
          </>
        )}
      </Box>
    </BlitzHeader>
  );
}